import React, { useState, useMemo } from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

const DEFAULT_COPY = {
  change: {
    headline: 'Choose a new password',
    subtitle:
      "This will be your password every time you log in. Save it somewhere safe before continuing — your old password won't work anymore.",
    submitLabel: 'Save and Continue',
  },
  setup: {
    headline: 'Set up your portal password',
    subtitle:
      "This is the password you'll use every time you log in. Save it somewhere safe before continuing.",
    submitLabel: 'Save and Continue',
  },
  reset: {
    headline: 'Choose a new password',
    subtitle:
      "Save it somewhere safe before continuing — you'll use it every time you log in.",
    submitLabel: 'Save and Continue',
  },
};

const PasswordSetForm = ({
  mode,
  onSubmit,
  headlineText,
  subtitleText,
  submitLabel,
}) => {
  const isChange = mode === 'change';
  const copy = DEFAULT_COPY[mode] || DEFAULT_COPY.reset;
  const headline = headlineText ?? copy.headline;
  const subtitle = subtitleText ?? copy.subtitle;
  const submitLabelResolved = submitLabel ?? copy.submitLabel;

  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [showNew, setShowNew] = useState(true);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirm, setShowConfirm] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const strength = useMemo(() => {
    if (newPassword.length === 0) return null;
    if (newPassword.length < 10) return 'short';
    if (!/(?=.*[\d\W_])/.test(newPassword)) return 'simple';
    return 'strong';
  }, [newPassword]);

  const matchState = useMemo(() => {
    if (confirmPassword.length === 0) return null;
    return newPassword === confirmPassword ? 'match' : 'mismatch';
  }, [newPassword, confirmPassword]);

  const passwordValid = strength === 'strong';
  const passwordsMatch = matchState === 'match';
  const currentFilled = !isChange || currentPassword.length > 0;
  const canSubmit =
    passwordValid && passwordsMatch && acknowledged && currentFilled && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) return;
    setLoading(true);
    try {
      await onSubmit(newPassword, isChange ? currentPassword : undefined);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const eyeButton = (visible, toggle) => (
    <button
      type="button"
      onClick={toggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-300 hover:text-white"
      aria-label={visible ? 'Hide password' : 'Show password'}
      tabIndex={-1}
    >
      {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">{headline}</h1>
        <p className="text-purple-200 text-sm leading-relaxed">{subtitle}</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {isChange && (
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            Current password
          </label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
              placeholder="Enter your current password"
            />
            {eyeButton(showCurrent, () => setShowCurrent(!showCurrent))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-purple-200 mb-2">
          {isChange ? 'New password' : 'Password'}
        </label>
        <div className="relative">
          <input
            type={showNew ? 'text' : 'password'}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={10}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
            placeholder="At least 10 characters with a number or symbol"
          />
          {eyeButton(showNew, () => setShowNew(!showNew))}
        </div>
        {strength && (
          <p
            className={`mt-2 text-sm ${
              strength === 'short'
                ? 'text-red-300'
                : strength === 'simple'
                ? 'text-yellow-300'
                : 'text-green-300'
            }`}
          >
            {strength === 'short' && 'Too short'}
            {strength === 'simple' && 'Add a number or symbol'}
            {strength === 'strong' && 'Strong'}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-purple-200 mb-2">
          {isChange ? 'Confirm new password' : 'Confirm password'}
        </label>
        <div className="relative">
          <input
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
            placeholder="Re-enter your password"
          />
          {eyeButton(showConfirm, () => setShowConfirm(!showConfirm))}
        </div>
        {matchState && (
          <p
            className={`mt-2 text-sm ${
              matchState === 'match' ? 'text-green-300' : 'text-red-300'
            }`}
          >
            {matchState === 'match' ? '✓ Matches' : "✗ Doesn't match"}
          </p>
        )}
      </div>

      <p className="text-xs text-purple-300/70">
        Your browser may offer to save this password. We recommend accepting.
      </p>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-white/30 bg-white/10 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
        />
        <span className="text-sm text-purple-200 select-none">
          I have saved this password somewhere I can find it
        </span>
      </label>

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {loading ? 'Saving...' : submitLabelResolved}
      </button>
    </form>
  );
};

export default PasswordSetForm;
